import { FC, useEffect, useState, useRef, useContext } from "react";
import { SignIn } from "./SignIn";
import { SignUp } from "./SignUp";
import { ChangePassword } from "./ChangePassword";
import { useStation } from "../../contexts/StationContext";
import { SocialUser } from "../../types/user";
import { auth, googleProvider, facebookProvider, twitterProvider } from "../../firebase";
import { signInWithPopup, GoogleAuthProvider, FacebookAuthProvider, TwitterAuthProvider } from "@firebase/auth";
import BrandLogo from '../../assets/logo.svg';
import Google from '../../assets/icons/google.svg?react';
import Facebook from '../../assets/icons/facebook-color.svg?react';
import X from '../../assets/icons/x.svg?react';
import { Button } from "../../components/ui/Button";
import { useAuth } from "../../contexts/AuthContext";
import { ForgotPassword } from "./ForgotPassword";

const Auth: FC = () => {
    const containerRef = useRef<HTMLDivElement>(null);
    const { station } = useStation();
    const { socialSignUpUser, socialSignInUser, error, currentScreen, setCurrentScreen, getMyLocation } = useAuth();

    const onCreateAccount = () => {
        setCurrentScreen(currentScreen === "signIn" ? "signUp" : "signIn");
        containerRef?.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }

    const handleSocialAuth = async (provider: GoogleAuthProvider | FacebookAuthProvider | TwitterAuthProvider, providerName: "Google" | "Facebook" | "Twitter") => {
        try {
            const result = await signInWithPopup(auth, provider);
            let token: string | null = null;
            if (providerName === "Google") {
                token = GoogleAuthProvider.credentialFromResult(result)?.accessToken || null;
            } else if (providerName === "Facebook") {
                token = FacebookAuthProvider.credentialFromResult(result)?.accessToken || null;
            } else if (providerName === "Twitter") {
                try {
                    token = await result.user.getIdToken();
                } catch (err) {
                    console.log("Error fetching token:", err);
                }
            }

            const email = result.user.email;
            const name = result.user.displayName;
            if (!token) return;
            const geoCoordinates = await getMyLocation();
            const request: SocialUser = {
                 provider: providerName, 
                 stationDetail: { stationId: station.id, stationName: station.name },
                 geoCoordinates: { latitude: geoCoordinates.latitude, longitude: geoCoordinates.longitude },
                 email: email || undefined,
                 name: name || undefined,
                 token
            };
            if (currentScreen === "signUp") {
                socialSignUpUser(request);
            } else {
                socialSignInUser(request);
            }
        } catch (err) {
            console.log("Error during authentication:", err);
        }
    };

    useEffect(() => {
        if (error?.includes('Subscriber Not Found')) {
            setCurrentScreen("signUp");
        } else if (error?.includes('Account already exists')) {
            setCurrentScreen("signIn");
        }
    }, [error]);

    return (
        <div ref={containerRef} className={`sm:w-[400px] ${currentScreen === "signIn" || currentScreen === "signUp" ? "py-4 px-4 sm:px-8" : "p-8"}`}>
            <div className="flex flex-col justify-start items-center gap-4 -mr-3 pr-3 sm:-mr-6 sm:pr-6 overflow-y-auto max-h-[92vh]">
                <img src={station?.logo || BrandLogo} srcSet={`${station?.logo || BrandLogo} 1x, ${station?.logo || BrandLogo} 2x`} loading="lazy" alt={station?.logoAlt || station?.name+"'s logo"} className="w-[92px] h-[80px] md:w-[104px] md:h-[88px]" />
                {currentScreen === "signIn" && <SignIn />}
                {currentScreen === "signUp" && <SignUp />}
                {currentScreen === "changePassword" && <ChangePassword />}
                {currentScreen === "forgotPassword" && <ForgotPassword />}
                {(currentScreen === "signIn" || currentScreen === "signUp") &&
                    <>
                        <div className="flex flex-col items-center gap-3 px-7">
                            <h5 className="text-center font-medium text-sm text-white">OR</h5>
                            <div className="flex gap-3 w-full">
                                <Button variant="plain" icon={<Google className="w-5 h-5" />} onClick={() => handleSocialAuth(googleProvider, "Google")} />
                                <Button variant="plain" icon={<Facebook className="w-5 h-5" />} onClick={() => handleSocialAuth(facebookProvider, "Facebook")} />
                                <Button variant="plain" icon={<X className="w-5 h-5 text-white" />} onClick={() => handleSocialAuth(twitterProvider, "Twitter")} />
                            </div>
                        </div>
                        <div className="text-center text-sm flex justify-center items-center gap-1 w-full">
                            <span className="text-white">{currentScreen === "signIn" ? "Don't have an account?" : "Already have an account?"}</span>
                            <button type="button"
                                onClick={onCreateAccount}
                                className="text-primary-600 underline hover:text-primary-700 font-semibold active:text-primary-800 focus:outline-none"
                            >
                                {currentScreen === "signIn" ? "Create" : "Log in"}
                            </button>
                        </div>
                    </>
                }
            </div>
        </div>
    );
};

export default Auth;